import React, { useEffect, useState } from 'react'
import { Button, Modal, Input, DatePicker } from 'antd'
import styled from 'styled-components'
import { useSelector, useDispatch } from 'react-redux'
import moment from 'moment'
import {
  fetchGrossProfitAndSales,
  sendSalesOrPurchaseData,
} from 'src/features/WeeklySalesSlice'
import { listRevenueCategories } from 'src/utils/revenueCategoryApi'
import RevenueDayEntry from 'src/screens/orders/RevenueDayEntry'
import dayjs from 'dayjs'

const SalesModal = ({ visibleSalesModal, setVisibleSalesModal, dateRange }) => {
  const [sales, setSales] = useState('')
  const [date, setDate] = useState(() => moment())
  const [revenueDate, setRevenueDate] = useState(() =>
    dayjs().format('YYYY-MM-DD'),
  )
  const [categories, setCategories] = useState(null)
  const venueId = useSelector(state => state.venue?.venue?._id)
  const dispatch = useDispatch()
  const activeCategories = (categories || []).filter(
    category => category.active !== false,
  )

  useEffect(() => {
    if (!visibleSalesModal || !venueId) return undefined
    let cancelled = false
    setCategories(null)
    listRevenueCategories(venueId)
      .then(data => {
        if (!cancelled) setCategories(Array.isArray(data) ? data : [])
        return null
      })
      .catch(() => {
        if (!cancelled) setCategories([])
      })
    return () => {
      cancelled = true
    }
  }, [visibleSalesModal, venueId])

  useEffect(() => {
    if (!visibleSalesModal) return undefined
    const onKeyDown = event => {
      if (event.key !== 'Escape') return
      resetForm()
      setVisibleSalesModal(false)
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [visibleSalesModal])

  const resetForm = () => {
    setSales('')
    setDate(moment())
  }

  const closeModal = () => {
    resetForm()
    setVisibleSalesModal(false)
  }

  const refreshGrossProfit = () => {
    if (!dateRange?.startDate || !dateRange?.endDate || !venueId) return
    dispatch(
      fetchGrossProfitAndSales(
        dateRange.startDate,
        dateRange.endDate,
        venueId,
        { silent: true },
      ),
    )
  }

  const onChangeDate = selectedDate => {
    setDate(selectedDate || null)
  }

  const usingCategories = activeCategories.length > 0

  return (
    <Modal
      width={usingCategories ? 720 : '40%'}
      title={
        usingCategories
          ? `Enter revenue data · ${dayjs(revenueDate).format(
              'dddd D MMM YYYY',
            )}`
          : `Enter revenue data · ${moment(date).format('dddd D MMM YYYY')}`
      }
      okText={'Save'}
      footer={
        categories == null || usingCategories ? (
          <Button onClick={closeModal}>Close</Button>
        ) : undefined
      }
      visible={visibleSalesModal}
      keyboard
      onOk={async () => {
        if (usingCategories || !date) return

        const body = {
          date: dayjs(date).format('YYYY-MM-DD'),
        }

        body.sale = Number(sales)
        body.isSale = true
        dispatch(sendSalesOrPurchaseData(venueId, body, dateRange))
        closeModal()
      }}
      onCancel={closeModal}
    >
      {categories == null ? (
        <CategoryHint>Loading categories…</CategoryHint>
      ) : usingCategories ? (
        <RevenueDayEntry
          venueId={venueId}
          presentation='modal'
          onDateChange={setRevenueDate}
          onSaved={() => {
            refreshGrossProfit()
            setVisibleSalesModal(false)
          }}
        />
      ) : (
        <SalesWrapper>
          <CategoryHint>
            Set up revenue categories on this page to enter them here. Until
            then, enter one sales figure.
          </CategoryHint>
          <DatePicker
            onChange={onChangeDate}
            value={date}
            style={{ width: '100%', marginBottom: 12 }}
          />
          <Input
            style={{ width: '100%' }}
            placeholder='Sales Figure'
            value={sales}
            onChange={event => {
              setSales(event.target.value)
            }}
          />
        </SalesWrapper>
      )}
    </Modal>
  )
}
const SalesWrapper = styled.div`
  display: flex;
  flex-direction: column;
`
const CategoryHint = styled.p`
  width: 100%;
  margin: 0 0 12px;
  color: #555555;
`
export default SalesModal
